import { useEffect, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { authStorageService } from '../../services/authStorageService';
import { Button } from '../../components/buttons/button';
import { Input } from '../../components/ui/input';
import { ICONS } from '../../constants/imageConstants';
import AuthAlerts from '../../components/common/AuthAlerts';
import { useAuthForm } from '../../hooks/useAuthForm';

export const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useContext(AuthContext);
  const from = (location.state && location.state.from && location.state.from.pathname) || '/dashboard';

  const {
    formData,
    submitting,
    error,
    setError,
    success,
    setSuccess,
    setFormData,
    onChange,
    showPassword,
    setShowPassword,
    handleSubmit
  } = useAuthForm({ email: '', password: '' }, 'login');

  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reason = params.get('reason');
    const email = params.get('email');

    if (reason === 'password_set') {
      setSuccess('Password set successfully! Please login with your new credentials.');
      if (email) {
        setFormData(prev => ({ ...prev, email }));
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (location.state?.successMessage) {
      setSuccess(location.state.successMessage);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location, setSuccess, setFormData]);

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  useEffect(() => {
    authStorageService.getRememberMeData().then(d => {
      if (d && d.email) {
        onChange({ target: { name: 'email', value: d.email } });
        if (d.password) onChange({ target: { name: 'password', value: d.password } });
        setRememberMe(true);
      }
    });
  }, []);

  const onSubmit = e => {
    handleSubmit(
      e,
      () => login({ login_id: formData.email, password: formData.password }, rememberMe),
      () => null
    );
  };

  const disabled = submitting || !formData.email || !formData.password;

  return (
    <div className="w-full">
      {(success || error) && (
        <div className="mb-6 animate-in fade-in slide-in-from-top-2">
          {success && <AuthAlerts type="success" message={success} onClear={() => setSuccess('')} />}
          {error && <AuthAlerts type="error" message={error} onClear={() => setError('')} />}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        <div className="space-y-4">
          <Input
            id="email"
            name="email"
            type="email"
            label="Email Address"
            autoComplete="email"
            value={formData.email}
            onChange={onChange}
            placeholder="name@organization.org"
            className="input-base"
          />
          
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              autoComplete="current-password"
              value={formData.password}
              onChange={onChange}
              placeholder="••••••••"
              className="input-base"
            />
            <button 
              type="button" 
              onClick={() => setShowPassword(!showPassword)} 
              className="absolute bottom-[13px] right-3 text-gray-400 hover:text-gray-600 rounded-md transition-colors" 
              tabIndex={-1}
            >
              <img src={showPassword ? ICONS.EYE : ICONS.EYE_OFF} alt="Toggle" className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center cursor-pointer select-none group">
            <div className="relative">
              <input 
                type="checkbox" 
                checked={!!rememberMe} 
                onChange={e => setRememberMe(e.target.checked)} 
                className="sr-only peer" 
              />
              <div className="w-5 h-5 border-2 border-gray-300 rounded transition-colors peer-checked:bg-gray-700 peer-checked:border-gray-700 group-hover:border-gray-500"></div>
              <svg className="absolute top-1 left-1 w-3 h-3 text-white hidden peer-checked:block pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="ml-2 text-sm font-medium text-gray-600 group-hover:text-gray-800">Keep me signed in</span>
          </label>
          
          <button 
            type="button" 
            onClick={() => navigate('/forgot-password')} 
            className="text-sm font-semibold text-blue-500 hover:underline underline-offset-2 focus:outline-none"
          >
            Forgot Password?
          </button>
        </div>

        <div className="pt-4">
          <Button 
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={disabled}
            loading={submitting}
          >
            {!submitting && (
              <div className="flex items-center gap-2">
                <img src={ICONS.LOGIN_BTN} alt="" className="h-4 w-4 brightness-0 invert" />
                <span>Sign In</span>
              </div>
            )}
            {submitting && <span>Signing In...</span>}
          </Button>
        </div>
      </form>
    </div>
  );
};